import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";
import { sendWelcomeEmail } from "../services/emails";

const AuthContext = createContext(null);

// Version of the legal agreements accepted at sign-up (Fase D2). Bump when the
// Terms / Privacy / Confidentiality documents change so re-acceptance can be required.
export const AGREEMENTS_VERSION = "1.0";

// Version of the designer payout policy (manual payouts, MVP). Bump on changes.
export const PAYOUT_POLICY_VERSION = "designer-payout-v1.0";

export const useAuth = () => useContext(AuthContext);

// Rejects if `promise` doesn't settle within `ms` — prevents the UI from hanging
// forever if a Firestore write stalls (this was the production onboarding freeze).
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(Object.assign(new Error("timeout"), { code: "timeout" })), ms)
    ),
  ]);
}

const PENDING_KEY = "dcc_pending_profile";

// Reads users/{uid} with a short retry so a single transient Firestore error
// (common on mobile networks, e.g. WHILE a long Gemini call is in flight) does
// NOT look like "profile missing". Returns the doc data, or null when the doc
// genuinely does not exist. Throws only after retries are exhausted (real error).
async function loadProfile(uid, { retries = 1 } = {}) {
  let lastErr;
  for (let i = 0; i <= retries; i += 1) {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      return snap.exists() ? snap.data() : null;
    } catch (e) {
      lastErr = e;
      if (i < retries) await new Promise((r) => setTimeout(r, 600));
    }
  }
  throw lastErr;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Firebase emailVerified as an explicit boolean. We do NOT read it off the `user`
  // object because spreading a Firebase User (e.g. {...currentUser}) drops the
  // emailVerified getter, leaving it undefined and pinning the banner open.
  const [emailVerified, setEmailVerified] = useState(false);
  // Distinguishes a *temporary* profile load failure from a genuinely missing
  // profile. When true, consumers know profile===null is transient, not "no doc".
  const [profileError, setProfileError] = useState(false);
  // True while register()/googleSignIn() are actively creating the account+profile.
  // The onAuthStateChanged listener fires the moment the Auth user is created —
  // BEFORE the Firestore doc exists — so its loadProfile() would return null and
  // clobber the profile that register() sets. This guard prevents that race.
  const authActionRef = useRef(false);
  // Mirror of the latest committed profile, readable synchronously inside the
  // auth listener. Used to avoid clobbering a good profile on token-refresh re-fires.
  const profileRef = useRef(null);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setUser(fbUser);
      setEmailVerified(!!(fbUser && fbUser.emailVerified));

      // Real sign-out: this is the ONLY place we clear the profile/session.
      if (!fbUser) {
        profileRef.current = null;
        setProfile(null);
        setProfileError(false);
        setLoading(false);
        return;
      }

      // During register/google sign-in the flow manages the profile itself.
      if (authActionRef.current) { setLoading(false); return; }

      const haveGoodProfile = profileRef.current && profileRef.current.uid === fbUser.uid;

      if (haveGoodProfile) {
        // Same user re-firing (e.g. periodic ID-token refresh during a long
        // Gemini call). We already have a valid profile — NEVER null it out.
        // Refresh in the background; keep the existing one on any failure.
        setLoading(false);
        try {
          const p = await loadProfile(fbUser.uid);
          if (p) { setProfile(p); setProfileError(false); }
          // p === null here would mean the doc vanished; ignore and keep the good one.
        } catch {
          /* transient: keep the existing good profile, do not clear */
        }
        return;
      }

      // First load for this user (no profile in memory yet).
      try {
        const p = await loadProfile(fbUser.uid);
        setProfile(p);            // null here = genuinely missing profile doc
        setProfileError(false);
      } catch {
        // Temporary load error: DON'T pretend the profile is missing. Leave it
        // as-is (null) but flag the error so guards can show a retry, not a logout.
        setProfileError(true);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (auth.currentUser) setProfile(await loadProfile(auth.currentUser.uid));
  }, []);

  const register = async (email, password, name, role, agreements, extra = {}) => {
    authActionRef.current = true;
    try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const data = {
      uid: cred.user.uid,
      email,
      name,
      role,
      createdAt: Date.now(),
      profileComplete: role === "lab",
      // Account identity / contact information captured at registration.
      accountType: extra.accountType || (role === "designer" ? "designer" : ""),
      accountTypeOther: extra.accountTypeOther || "",
      contactName: extra.contactName || "",
      phone: extra.phone || "",
      address: {
        street: extra.address?.street || "",
        city: extra.address?.city || "",
        state: extra.address?.state || "",
        postalCode: extra.address?.postalCode || "",
        country: extra.address?.country || "",
      },
      agreements: {
        version: AGREEMENTS_VERSION,
        acceptedAt: Date.now(),
        tos: true,
        privacy: true,
        confidentiality: true,
      },
    };
    // Designers must accept the payout policy at sign-up (manual payouts, MVP).
    if (role === "designer") {
      data.payoutPolicy = {
        accepted: true,
        policyVersion: PAYOUT_POLICY_VERSION,
        acceptedAt: Date.now(),
      };
    }
    // Persist a recovery copy BEFORE the write so that, if the Firestore write
    // fails/stalls after Auth already created the account, the next login can
    // finish setting up the profile with the exact same data (idempotent).
    try { localStorage.setItem(PENDING_KEY, JSON.stringify(data)); } catch { /* ignore */ }
    try {
      await withTimeout(setDoc(doc(db, "users", cred.user.uid), data, { merge: true }), 12000);
    } catch (e) {
      // Auth account exists but the profile could not be saved. Sign out for a clean
      // state and surface a typed error so the UI shows a clear, non-technical message.
      try { await signOut(auth); } catch { /* ignore */ }
      throw Object.assign(new Error("profile-setup-failed"), { code: "profile-setup-failed" });
    }
    try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
    // Best-effort onboarding emails (never block or fail the signup):
    // 1) Firebase email verification, 2) bilingual welcome email (Resend backend).
    try { await sendEmailVerification(cred.user); } catch { /* ignore */ }
    sendWelcomeEmail({ email, name, role, originUrl: window.location.origin });
    setProfile(data);
    return data;
    } finally {
      authActionRef.current = false;
    }
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    let p = await loadProfile(cred.user.uid);
    // Recover a profile from a registration whose Firestore write had failed.
    if (!p) {
      try {
        const raw = localStorage.getItem(PENDING_KEY);
        const pend = raw ? JSON.parse(raw) : null;
        if (pend && pend.uid === cred.user.uid) {
          await setDoc(doc(db, "users", cred.user.uid), pend, { merge: true });
          p = pend;
        }
      } catch { /* best-effort recovery */ }
    }
    // Any-device recovery: the Auth account exists but there is NO Firestore doc
    // and no local pending copy (e.g. registration froze on another device). Create
    // a minimal, idempotent profile so the user is never stuck and shows in Admin.
    // They can complete details later (profileComplete stays false).
    if (!p) {
      try {
        const minimal = {
          uid: cred.user.uid,
          email: cred.user.email || email,
          name: cred.user.displayName || cred.user.email || email,
          role: "lab",
          createdAt: Date.now(),
          profileComplete: false,
          recovered: true,
          recoveredAt: Date.now(),
        };
        await setDoc(doc(db, "users", cred.user.uid), minimal, { merge: true });
        p = minimal;
      } catch { /* best-effort recovery */ }
    }
    try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
    setProfile(p);
    return p;
  };

  const googleSignIn = async (role, agreements) => {
    authActionRef.current = true;
    try {
    const cred = await signInWithPopup(auth, googleProvider);
    let p = await loadProfile(cred.user.uid);
    if (!p) {
      p = {
        uid: cred.user.uid,
        email: cred.user.email,
        name: cred.user.displayName || cred.user.email,
        role: role || "lab",
        createdAt: Date.now(),
        profileComplete: (role || "lab") === "lab",
        agreements: {
          version: AGREEMENTS_VERSION,
          acceptedAt: Date.now(),
          tos: true,
          privacy: true,
          confidentiality: true,
        },
      };
      await setDoc(doc(db, "users", cred.user.uid), p);
      sendWelcomeEmail({ email: p.email, name: p.name, role: p.role, originUrl: window.location.origin });
    }
    setProfile(p);
    return p;
    } finally {
      authActionRef.current = false;
    }
  };

  // Firebase email verification — user can request / re-send at any time.
  const sendVerification = async () => {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser);
  };

  // Refreshes emailVerified after the user clicks the verification link. Forces a
  // reload + fresh ID token so Firebase reports the latest server-side state, then
  // updates the explicit `emailVerified` boolean (triggers a re-render even though
  // reload() mutates the same currentUser object in place). Returns the new value.
  const reloadUser = async () => {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    try { await auth.currentUser.getIdToken(true); } catch { /* ignore token refresh errors */ }
    const verified = !!auth.currentUser.emailVerified;
    setUser(auth.currentUser);
    setEmailVerified(verified);
    return verified;
  };

  // Standard Firebase password reset (Forgot password). No custom email system.
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const logout = async () => {
    await signOut(auth);
    setProfile(null);
  };

  const value = {
    user,
    profile,
    role: profile?.role,
    loading,
    profileError,
    emailVerified,
    register,
    login,
    googleSignIn,
    logout,
    refreshProfile,
    sendVerification,
    reloadUser,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
